import React, { useState, useMemo } from 'react';
import { Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../context/ThemeContext';
import Table from '../../../commonComponents/Table';
import TablePagination from '../../../commonComponents/Pagination';
import { ActionIconButton, AddressText, TableCard, TableWrapper } from '../styles';
import type { StoreTableProps } from '../types';
import type { ColumnsType } from 'antd/es/table';
import type { Store } from '../../../redux/UserStores';

const StoreTable: React.FC<StoreTableProps> = ({ stores, loading, onEdit, onDelete }) => {
  const { isDarkMode } = useTheme();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  
  const paginatedStores = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return stores.slice(start, end);
  }, [stores, currentPage, pageSize]);

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    }
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const columns: ColumnsType<Store> = [
    {
      title: 'Store Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Address',
      key: 'address',
      render: (_, record: Store) => (
        <div>
          <div>{record.address || 'No address provided'}</div>
          {record.city && record.state && (
            <AddressText>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {record.city}, {record.state} {record.zipCode}
            </AddressText>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record: Store) => (
        <Space size="small">
          <ActionIconButton
            onClick={() => onEdit(record)}
            iconColor="#2D6CCA"
          >
            <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
          </ActionIconButton>
          <ActionIconButton
            onClick={() => onDelete(record.id)}
            iconColor="#f5222d"
          >
            <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
          </ActionIconButton>
        </Space>
      ),
    },
  ];

  return (
    <TableWrapper>
      <TableCard>
        <Table<Store>
          columns={columns}
          dataSource={paginatedStores}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={stores.length > 0 ? { x: true, y: 600 } : undefined}
          emptyText="No stores found"
          headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
          removeVerticalBorders={true}
          thPadding="15px 10px"
          tdPadding="10px 10px"
          firstChildBorderRadius="8px"
          border="0"
          borderBottomLeftRight={true}
          theme={isDarkMode ? 'dark' : 'light'}
        />
      </TableCard>
      {stores.length > 0 && (
        <TablePagination
          totalCount={stores.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total stores"
          padding="16px 12px 0px 12px"
        />
      )}
    </TableWrapper>
  );
};

export default React.memo(StoreTable);
